import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  getDaysInMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns'
import type { CellState, GridCell, GridRow, Granularity, LifeProfile, LifeStats, Milestone } from '../types/life'

export const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export const MONTHS_FULL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/** "Friday, 28 August" */
export function formatDayLong(d: Date): string {
  return `${WEEKDAYS[d.getDay()]}, ${d.getDate()} ${MONTHS_FULL[d.getMonth()]}`
}

/** "12 — 18 May 2026" / "28 April — 4 May 2026" */
export function formatDayRange(start: Date, endExclusive: Date): string {
  const last = new Date(endExclusive.getTime() - 1)
  const sameMonth = start.getMonth() === last.getMonth() && start.getFullYear() === last.getFullYear()
  if (sameMonth) {
    return `${start.getDate()} — ${last.getDate()} ${MONTHS_FULL[start.getMonth()]} ${start.getFullYear()}`
  }
  const sameYear = start.getFullYear() === last.getFullYear()
  const left = `${start.getDate()} ${MONTHS_FULL[start.getMonth()]}${sameYear ? '' : ` ${start.getFullYear()}`}`
  return `${left} — ${last.getDate()} ${MONTHS_FULL[last.getMonth()]} ${last.getFullYear()}`
}

/** How many years ahead the year strip on the "Now" view shows. */
export const YEARS_AHEAD = 10
export const STRIP_COLUMNS = 14
export const DAY_COLUMNS = 31
/** Weeks per year of life on the "Whole life" view. */
export const SLICES_PER_YEAR = 52

const WEEK_MS = 604_800_000
const DAY_MS = 86_400_000

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

export function parseDate(iso: string): Date {
  return startOfDay(new Date(`${iso}T00:00:00`))
}

export function toISODate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function birthOf(profile: LifeProfile): Date {
  return parseDate(profile.birthDate)
}

export function horizonOf(profile: LifeProfile): Date {
  return addYears(birthOf(profile), profile.lifeExpectancy)
}

function stateOf(start: Date, end: Date, now: Date, birth: Date, horizon: Date): CellState {
  if (end.getTime() <= birth.getTime() || start.getTime() >= horizon.getTime()) return 'void'
  if (end.getTime() <= now.getTime()) return 'past'
  if (start.getTime() <= now.getTime()) return 'current'
  return 'future'
}

function fractionOf(cells: GridCell[], now: Date): number {
  let last = -1
  let partial = 0
  cells.forEach((cell, i) => {
    if (cell.state === 'past') {
      last = i
      partial = 1
    } else if (cell.state === 'current') {
      last = i
      const span = cell.end.getTime() - cell.start.getTime()
      partial = span > 0 ? Math.min(1, Math.max(0, (now.getTime() - cell.start.getTime()) / span)) : 0
    }
  })
  if (last < 0) return 0
  return (last + partial) / cells.length
}

function voidCell(key: string, anchor: Date): GridCell {
  return { key, label: '', start: anchor, end: anchor, state: 'void' }
}

/* ─────────────── "Now" view: the year strip ─────────────── */

export function buildYearStrip(profile: LifeProfile, now: Date, columns = STRIP_COLUMNS): GridRow[] {
  const birth = birthOf(profile)
  const horizon = horizonOf(profile)
  const from = birth.getFullYear()
  const to = Math.min(now.getFullYear() + YEARS_AHEAD, horizon.getFullYear())
  const years: number[] = []
  for (let y = from; y <= to; y++) years.push(y)

  const rows: GridRow[] = []
  for (let i = 0; i < years.length; i += columns) {
    const chunk = years.slice(i, i + columns)
    const cells: GridCell[] = chunk.map((y) => {
      const start = new Date(y, 0, 1)
      const end = new Date(y + 1, 0, 1)
      return { key: `y-${y}`, label: String(y), start, end, state: stateOf(start, end, now, birth, horizon) }
    })
    while (cells.length < columns) {
      cells.push(voidCell(`y-void-${i}-${cells.length}`, new Date(chunk[0], 0, 1)))
    }
    rows.push({ key: `strip-${chunk[0]}`, label: '', cells, fraction: fractionOf(cells, now) })
  }
  return rows
}

/* ─────────────── Вьюха «Сейчас»: текущий год по дням ─────────────── */

export function buildMonthDayRows(profile: LifeProfile, now: Date, year: number): GridRow[] {
  const birth = birthOf(profile)
  const horizon = horizonOf(profile)
  return MONTHS_SHORT.map((label, m) => {
    const total = getDaysInMonth(new Date(year, m, 1))
    const cells: GridCell[] = Array.from({ length: DAY_COLUMNS }, (_, i) => {
      const day = i + 1
      if (day > total) return voidCell(`d-void-${year}-${pad(m + 1)}-${day}`, new Date(year, m, total))
      const start = new Date(year, m, day)
      const end = addDays(start, 1)
      return {
        key: `d-${year}-${pad(m + 1)}-${pad(day)}`,
        label: String(day),
        start,
        end,
        state: stateOf(start, end, now, birth, horizon),
      }
    })
    return {
      key: `m-${year}-${pad(m + 1)}`,
      label,
      cells,
      fraction: fractionOf(cells, now),
    }
  })
}

/* ─────────────── Вьюха «Вся жизнь»: недели жизни ─────────────── */

export function buildLifeWeekRows(profile: LifeProfile, now: Date): GridRow[] {
  const birth = birthOf(profile)
  const horizon = horizonOf(profile)
  return Array.from({ length: profile.lifeExpectancy }, (_, age) => {
    const yearStart = addYears(birth, age)
    const yearEnd = addYears(birth, age + 1)
    const span = (yearEnd.getTime() - yearStart.getTime()) / SLICES_PER_YEAR
    const cells: GridCell[] = Array.from({ length: SLICES_PER_YEAR }, (_, k) => {
      const start = new Date(yearStart.getTime() + k * span)
      const end = new Date(yearStart.getTime() + (k + 1) * span)
      return {
        key: `lw-${age}-${k}`,
        label: String(k + 1),
        start,
        end,
        state: stateOf(start, end, now, birth, horizon),
      }
    })
    return {
      key: `life-${age}`,
      label: String(age),
      subLabel: String(yearStart.getFullYear()),
      cells,
      fraction: fractionOf(cells, now),
    }
  })
}

export function lifeWeekKeyOf(profile: LifeProfile, date: Date): string | null {
  const birth = birthOf(profile)
  if (date.getTime() < birth.getTime()) return null
  let age = date.getFullYear() - birth.getFullYear()
  if (addYears(birth, age).getTime() > date.getTime()) age -= 1
  if (age < 0 || age >= profile.lifeExpectancy) return null
  const yearStart = addYears(birth, age)
  const span = (addYears(birth, age + 1).getTime() - yearStart.getTime()) / SLICES_PER_YEAR
  const k = Math.min(SLICES_PER_YEAR - 1, Math.max(0, Math.floor((date.getTime() - yearStart.getTime()) / span)))
  return `lw-${age}-${k}`
}

/* ─────────────── Майлстоуны ─────────────── */

export function milestoneStart(m: Milestone): Date {
  const d = parseDate(m.date)
  if (m.granularity === 'years') return startOfYear(d)
  if (m.granularity === 'months') return startOfMonth(d)
  if (m.granularity === 'weeks') return startOfWeek(d, { weekStartsOn: 1 })
  return startOfDay(d)
}

export function milestoneEnd(m: Milestone): Date {
  const start = milestoneStart(m)
  if (m.granularity === 'years') return addYears(start, 1)
  if (m.granularity === 'months') return addMonths(start, 1)
  if (m.granularity === 'weeks') return addWeeks(start, 1)
  return addDays(start, 1)
}

export function milestoneUnitLabel(m: Milestone): string {
  const d = parseDate(m.date)
  if (m.granularity === 'years') return String(d.getFullYear())
  if (m.granularity === 'months') return `${MONTHS_FULL[d.getMonth()]} ${d.getFullYear()}`
  if (m.granularity === 'days') return `${d.getDate()} ${MONTHS_FULL[d.getMonth()]} ${d.getFullYear()}`
  const monday = startOfWeek(d, { weekStartsOn: 1 })
  return formatDayRange(monday, addDays(monday, 7))
}

/** Клетки, на которых майлстоун должен быть виден: свой уровень и все более крупные. */
export function milestoneCellKeys(profile: LifeProfile, m: Milestone): string[] {
  const d = parseDate(m.date)
  const keys = [`y-${d.getFullYear()}`]
  if (m.granularity !== 'years') keys.push(`m-${d.getFullYear()}-${pad(d.getMonth() + 1)}`)
  if (m.granularity === 'days') keys.push(`d-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`)
  const lifeWeek = lifeWeekKeyOf(profile, d)
  if (lifeWeek) keys.push(lifeWeek)
  return keys
}

/** Ключ клетки, к которой нужно проскроллить/подсветить при выборе майлстоуна. */
export function milestoneFocusKey(profile: LifeProfile, m: Milestone): string | null {
  const d = parseDate(m.date)
  if (m.granularity === 'years') return `y-${d.getFullYear()}`
  if (m.granularity === 'months') return `m-${d.getFullYear()}-${pad(d.getMonth() + 1)}`
  if (m.granularity === 'days') return `d-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  return lifeWeekKeyOf(profile, d)
}

/* ─────────────── Цифры ─────────────── */

export function computeStats(profile: LifeProfile, now: Date): LifeStats {
  const birth = birthOf(profile)
  const horizon = horizonOf(profile)
  const total = horizon.getTime() - birth.getTime()
  const lived = Math.max(0, Math.min(total, now.getTime() - birth.getTime()))
  const left = Math.max(0, total - lived)
  return {
    percentLived: total > 0 ? (lived / total) * 100 : 0,
    weeksLived: Math.floor(lived / WEEK_MS),
    weeksLeft: Math.floor(left / WEEK_MS),
    daysLived: Math.floor(lived / DAY_MS),
    daysLeft: Math.floor(left / DAY_MS),
  }
}
